<template>
  <div class="best-offer-configuration-text-container">
    <div class="best-offer-text-section">
      <div class="best-offer-text-header header-content">
        <div class="text-header-title">
          <mf-icon icon="data_object" color="#962EE7" size="20" />
          <span>Melhor oferta</span>
        </div>
        <mf-tooltip bottom variant="secondary" max-width="416px">
          <template v-slot:activator="{ on }">
            <mf-icon color="cinza-l20" size="20" icon="info" fill v-on="on" />
          </template>
          <span class="best-offer-tooltip" v-html="bestOfferTooltipText" />
        </mf-tooltip>
      </div>

      <div class="editable-content-section">
        <div class="d-flex flex-row justify-space-between align-center">
          <variables-menu label="Adicionar subvariável" variant="subvariables" :items="countedSubvariables" @set-variable="insertSubvariable" />
          <div class="reset-default-section" @click="resetDefaultMessage">
            <mf-icon color="marca-0-l30" size="20" icon="autorenew" />
            <span>Redefinir padrão</span>
          </div>
        </div>
        <input-with-subvariables
          ref="productsComponent"
          v-model="repeatableText"
          inputType="products"
          :offer-type="offerType"
          :error="errorRepeatableText.length > 0"
          :error-message="errorRepeatableText"
        />
        <input-with-subvariables
          ref="validityComponent"
          v-model="validityText"
          inputType="validity"
          :offer-type="offerType"
          :error="errorValidityText.length > 0"
          :error-message="errorValidityText"
        />
      </div>
      <div class="best-offer-text-info">
        <mf-icon icon="info" color="cinza-l20" size="16" />
        <span v-html="bestOfferInfoText"></span>
      </div>
    </div>

    <div class="best-offer-inpection-section">
      <div class="best-offer-inspection-header header-content">
        <span class="inspection-header-title">Inspeção</span>
        <span class="inspection-header-char-count">{{ charCount }} caracteres</span>
      </div>
      <best-offer-configuration-preview :message="combinedMessage" :channel="channel" :offer-type="offerType" is-inspection-inside-best-offer />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getValiditySubvariablesTitle, subvariablesData, getChannelsDefaultVariablesMessages } from '@/modules/maria/helpers/variables.js'
import InputWithSubvariables from './InputWithSubvariables.vue'
import VariablesMenu from '@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/VariablesMenu.vue'
import BestOfferConfigurationPreview from './BestOfferConfigurationPreview.vue'
import { charCount } from '@/modules/maria/campanha-da-maria-v2/mixins/char-count'

export default {
  name: 'BestOfferConfigurationText',
  components: { InputWithSubvariables, VariablesMenu, BestOfferConfigurationPreview },
  mixins: [charCount],
  props: {
    offerType: {
      type: String,
      default: 'offer_sales',
      validator: (value) =>
        [
          'offer_sales',
          'percentage_offer_sales',
          'cashback_absolute_offer_sales',
          'cashback_percentage_offer_sales',
          'takepay_offer_sales',
          'points_offer_sales',
          'combo_offer_sales',
        ].includes(value),
    },
    channel: {
      type: String,
      default: 'sms',
      validator: (value) => ['sms', 'push', 'whatsapp', 'email'].includes(value),
    },
    field: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'title', 'inner_message_title', 'inner_message_text'].includes(value),
    },
    errorRepeatableText: {
      type: Array,
      default: () => [],
    },
    errorValidityText: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    charCount: 0,
    localMessage: {},
  }),
  computed: {
    ...mapGetters(['campaignVariablesConfiguration', 'getVariableCharCount']),
    bestOfferTooltipText() {
      return 'Ao adicionar a variável <span class="best-offer-tooltip-highlight">Melhor oferta</span> em uma mensagem, você pode selecionar quantas ofertas serão enviadas. O texto que se refere às informações do produto será replicado pela quantidade selecionada, já a data de validade é exibida uma única vez.'
    },
    bestOfferInfoText() {
      return 'A subvariável <span class="best-offer-info-highlight">{regra mín/máx}</span> é dinâmica, portanto para produtos que não possuem mínimo ou máximo ela é ignorada.'
    },
    countedSubvariables() {
      return subvariablesData
        .filter((subvar) => subvar.availableFor.includes('all') || subvar.availableFor.includes(this.offerType))
        .map((subvar) => {
          return { ...subvar, charCount: this.getVariableCharCount(subvar.title) }
        })
    },
    repeatableText: {
      get() {
        return this.localMessage?.[this.channel]?.[this.field]?.[this.offerType]?.repeatable_text ?? ''
      },
      set(value) {
        this.localMessage[this.channel][this.field][this.offerType].repeatable_text = value
      },
    },
    validityText: {
      get() {
        return this.localMessage?.[this.channel]?.[this.field]?.[this.offerType]?.validity_text ?? ''
      },
      set(value) {
        this.localMessage[this.channel][this.field][this.offerType].validity_text = value
      },
    },
    combinedMessage() {
      return this.repeatableText + this.validityText
    },
  },
  watch: {
    combinedMessage(newValue) {
      this.countCharacters()
      this.$emit('change', newValue)
    },
  },
  mounted() {
    this.localMessage = JSON.parse(JSON.stringify(this.campaignVariablesConfiguration))
    this.countCharacters()
  },
  methods: {
    countCharacters() {
      this.charCount = this.countTextWithSubvariables(this.combinedMessage)
    },
    insertSubvariable(text) {
      const insertInValidity = getValiditySubvariablesTitle().includes(text)

      if (insertInValidity) {
        this.$refs.validityComponent.insertSubvariable(text)
      } else {
        this.$refs.productsComponent.insertSubvariable(text)
      }
    },
    resetDefaultMessage() {
      const message = getChannelsDefaultVariablesMessages()[this.channel][this.field][this.offerType]
      this.localMessage[this.channel][this.field][this.offerType] = message
    },
  },
}
</script>

<style lang="scss" scoped>
.best-offer-configuration-text-container {
  background-color: $cinza-l90;
  display: flex;
  flex-direction: row;
  gap: 20px;
  width: 100%;
  padding: 16px 20px;
  .header-content {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .best-offer-text-section {
    width: 50%;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .best-offer-text-header {
      .text-header-title {
        display: flex;
        flex-direction: row;
        align-items: flex-end;

        span {
          color: #962ee7;
          font-weight: 700;
          font-size: 16px;
          letter-spacing: 0.4px;
          line-height: 23px;
          margin-left: 4px;
        }
      }
    }

    .best-offer-text-info {
      width: 100%;
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 6px;
      .icon-wrapper {
        display: flex;
        align-items: flex-start;
        height: 16px !important;
      }
      span {
        color: $cinza-0;
        font-weight: 400;
        font-size: 12px;
        line-height: 14.4px;
        letter-spacing: 1%;
      }
    }
  }

  .best-offer-inpection-section {
    width: 50%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    .best-offer-inspection-header {
      .inspection-header-title {
        color: $cinza-0;
        font-weight: 700;
        font-size: 16px;
        letter-spacing: 0.4px;
      }
      .inspection-header-char-count {
        color: #656565;
        font-weight: 400;
        font-size: 14px;
        letter-spacing: 0.23px;
        text-align: right;
      }
    }
  }
}

.reset-default-section {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;

  span {
    color: $marca-0-l30;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }
}

.editable-content-section {
  width: 100%;
  height: 200px;
  overflow: auto;
  border: 1px solid $cinza-l60;
  border-radius: 3px;
  padding: 10px 18px;
  outline: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #ffffff;
}

::v-deep .best-offer-tooltip-highlight {
  font-weight: 700;
  color: #962ee7;
}
::v-deep .best-offer-info-highlight {
  font-weight: 700;
  color: $cinza-d20;
}
</style>
